// Shared types, constants and helpers for the Email Management feature.
// Requests themselves go through the project's `useRequest` hook; this module
// only centralises the contract so every screen agrees on it.

export type CampaignStatus =
  | "draft"
  | "pending_approval"
  | "scheduled"
  | "sending"
  | "sent"
  | "cancelled";

export type TemplateStatus = "draft" | "approved";

export type SendStatus =
  | "queued"
  | "sending"
  | "sent"
  | "failed"
  | "skipped"
  | "cancelled";

export type UserType = "buyer" | "merchant";
export type UserStatus = "active" | "inactive";

export interface AudienceFilter {
  userTypes?: UserType[];
  statuses?: UserStatus[];
  emailVerified?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  includeNewsletterSubscribers?: boolean;
}

export interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  subject: string;
  bodyHtml: string;
  status: TemplateStatus;
  lastEditedBy?: string;
  updated_at?: string;
  created_at?: string;
}

export interface EmailCampaign {
  id: string;
  name: string;
  category: string;
  templateId?: string | null;
  subject?: string;
  bodyHtml?: string;
  audienceFilter?: AudienceFilter | null;
  status: CampaignStatus;
  scheduledAt?: string | null;
  // Aggregate delivery figures carried on the campaign record itself
  recipientCount?: number | null;
  sentCount?: number | null;
  failedCount?: number | null;
  bodyText?: string | null;
  createdBy?: string;
  approvedBy?: string | null;
  approvedAt?: string | null;
  cancelledAt?: string | null;
  updated_at?: string;
  created_at?: string;
}

// Shape returned by both /preview endpoints
export interface EmailPreview {
  subject: string;
  html: string;
  text: string;
  variables: Record<string, string>;
}

export interface AudiencePreview {
  count: number;
  sample: string[];
}

// Only these may appear in subject/bodyHtml — anything else is a 400
export const TEMPLATE_VARIABLES = [
  "first_name",
  "app_download_url",
  "app_url",
  "catalog_url",
] as const;

// Known categories; the editors also accept a free-text value
export const KNOWN_CATEGORIES = ["newsletter", "announcement"] as const;

// A campaign may only be built from these template categories — onboarding
// templates belong to the transactional flow and must not be offered here.
export const CAMPAIGN_TEMPLATE_CATEGORIES = ["newsletter", "announcement"];

// Validation failures come back with `message` as an array; flatten it so the
// toast shows one readable string.
export const toMessage = (res: any, fallback: string): string => {
  const m = res?.message;
  if (Array.isArray(m)) return m.filter(Boolean).join(", ") || fallback;
  return typeof m === "string" && m ? m : fallback;
};

export const prettyStatus = (s?: string) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  pending_approval: "bg-amber-50 text-amber-700",
  approved: "bg-green-50 text-green-700",
  scheduled: "bg-blue-50 text-blue-700",
  sending: "bg-sky-50 text-sky-700",
  sent: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-600",
  queued: "bg-gray-100 text-gray-600",
  failed: "bg-red-50 text-red-600",
  skipped: "bg-gray-100 text-gray-500",
};

export const statusClass = (s?: string) =>
  STATUS_STYLES[s || ""] || "bg-gray-100 text-gray-600";

// One-line summary of an audience filter, for list rows
export const audienceSummary = (f?: AudienceFilter | null): string => {
  if (!f) return "—";
  const parts: string[] = [];
  if (f.userTypes?.length)
    parts.push(f.userTypes.map((t) => `${prettyStatus(t)}s`).join(" + "));
  if (f.statuses?.length) parts.push(f.statuses.map(prettyStatus).join(" + "));
  if (f.emailVerified === true) parts.push("Verified");
  if (f.emailVerified === false) parts.push("Unverified");
  if (f.includeNewsletterSubscribers) parts.push("+ Subscribers");
  return parts.length ? parts.join(" · ") : "All users";
};

// The signed-in admin, used to grey out approvals the API would reject with a
// 403 (an admin may not approve their own work). The server is authoritative.
export const currentAdminId = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}")?.id;
  } catch {
    return undefined;
  }
};
